import { createBrowserRouter, RouteObject } from "react-router-dom";

import { ROUTES } from "@/shared/model/routes";

import { App } from "./app";
import {
  GuestRoute,
  guestLoader,
  protectedLoader,
  ProtectedRoute,
} from "./protected-route";
import { Providers } from "./providers";

const appElement = (
  <Providers>
    <App />
  </Providers>
);

const publicRoutes: RouteObject[] = [
  {
    index: true,
    lazy: () => import("@/features/home/home.page"),
    handle: { title: "Главная" },
  },
  {
    path: ROUTES.PRODUCTS,
    lazy: () => import("@/features/products/products.page"),
    handle: { title: "Товары" },
  },
  {
    path: ROUTES.PRODUCT,
    lazy: () => import("@/features/product/product.page"),
    handle: { title: "Товар" },
  },
  {
    path: ROUTES.STORE,
    lazy: () => import("@/features/store/store.page"),
    handle: { title: "Магазин" },
  },
  {
    path: ROUTES.FAQ,
    lazy: () => import("@/features/faq/faq.page"),
    handle: { title: "FAQ" },
  },
  {
    path: ROUTES.USER_RULES,
    lazy: () => import("@/features/faq/user-rules.page"),
    handle: { title: "Правила покупателя" },
  },
  {
    path: ROUTES.USER_HOW_TO_BUY,
    lazy: () => import("@/features/faq/user-how-to-buy.page"),
    handle: { title: "Как покупать" },
  },
  {
    path: ROUTES.USER_GUARANTEES,
    lazy: () => import("@/features/faq/user-guarantees.page"),
    handle: { title: "Гарантии" },
  },
  {
    path: ROUTES.STORE_BECOME_STORE,
    lazy: () => import("@/features/become-store/become-store.page"),
    handle: { title: "Стать продавцом" },
  },
  {
    path: ROUTES.STORE_HOW_TO_SELL,
    lazy: () => import("@/features/faq/store-how-to-sell.page"),
    handle: { title: "Как продавать" },
  },
  {
    path: ROUTES.STORE_GUARANTEES,
    lazy: () => import("@/features/faq/store-guarantees.page"),
    handle: { title: "Гарантии продавца" },
  },
  {
    path: ROUTES.STORE_RULES,
    lazy: () => import("@/features/faq/store-rules.page"),
    handle: { title: "Правила продавца" },
  },
  {
    path: ROUTES.STORE_COMMISSION,
    lazy: () => import("@/features/faq/store-finance.page"),
    handle: { title: "Финансы" },
  },
  {
    path: ROUTES.PRIVACY_POLICY,
    lazy: () => import("@/features/legal/privacy-policy.page"),
    handle: { title: "Политика конфиденциальности" },
  },
  {
    path: ROUTES.TERMS_OF_USE,
    lazy: () => import("@/features/legal/terms-of-use.page"),
    handle: { title: "Условия использования" },
  },
  {
    path: ROUTES.TERMS_AND_CONDITIONS,
    lazy: () => import("@/features/legal/terms-and-conditions.page"),
    handle: { title: "Условия и положения" },
  },
  {
    path: "*",
    lazy: () => import("@/features/home/home.page"),
    handle: { title: "Главная" },
  },
];

const guestRoutes: RouteObject[] = [
  {
    path: ROUTES.LOGIN,
    lazy: () => import("@/features/auth/login.page"),
    handle: { title: "Вход" },
  },
  {
    path: ROUTES.REGISTER,
    lazy: () => import("@/features/auth/register.page"),
    handle: { title: "Регистрация" },
  },
  {
    path: ROUTES.RESET_PASSWORD,
    lazy: () => import("@/features/auth/reset-password.page"),
    handle: { title: "Восстановление пароля" },
  },
];

const protectedRoutes: RouteObject[] = [
  {
    path: ROUTES.DASHBOARD,
    lazy: () => import("@/features/dashboard/dashboard.page"),
    handle: { title: "Личный кабинет" },
  },
  {
    path: ROUTES.TICKETS,
    lazy: () => import("@/features/tickets/tickets.layout"),
    handle: { title: "Тикеты" },
    children: [
      {
        index: true,
        lazy: () => import("@/features/tickets/tickets-index.page"),
        handle: { title: "Тикеты" },
      },
      {
        path: ":id",
        lazy: () => import("@/features/ticket/ticket.page"),
        handle: { title: "Тикет" },
      },
    ],
  },
  {
    path: ROUTES.PURCHASES,
    lazy: () => import("@/features/purchases/purchases.page"),
    handle: { title: "Покупки" },
  },
  {
    path: ROUTES.DEPOSITS,
    lazy: () => import("@/features/deposits/deposits.page"),
    handle: { title: "Пополнения" },
  },
  {
    path: ROUTES.DISPUTES,
    lazy: () => import("@/features/disputes/disputes.page"),
    handle: { title: "Диспуты" },
  },
  {
    path: ROUTES.STORE_DASHBOARD,
    lazy: () => import("@/features/store-dashboard/store-dashboard.page"),
    handle: { title: "Кабинет продавца" },
  },
  {
    path: ROUTES.STORE_PRODUCTS,
    lazy: () => import("@/features/store-products/store-products.page"),
    handle: { title: "Мои товары" },
  },
  {
    path: ROUTES.STORE_PRODUCT,
    lazy: () => import("@/features/store-product/store-product.page"),
    handle: { title: "Редактирование товара" },
  },
  {
    path: ROUTES.SALES,
    lazy: () => import("@/features/sales/sales.page"),
    handle: { title: "Продажи" },
  },
  {
    path: ROUTES.WITHDRAWALS,
    lazy: () => import("@/features/withdrawals/withdrawals.page"),
    handle: { title: "Выводы" },
  },
  {
    path: ROUTES.STORE_REVIEWS,
    lazy: () => import("@/features/store-reviews/store-reviews.page"),
    handle: { title: "Отзывы" },
  },
  {
    path: ROUTES.STORE_DISPUTES,
    lazy: () => import("@/features/store-disputes/store-disputes.page"),
    handle: { title: "Диспуты продавца" },
  },
];

export const router = createBrowserRouter([
  {
    element: appElement,
    children: [
      ...publicRoutes,
      {
        loader: guestLoader,
        element: <GuestRoute />,
        children: guestRoutes,
      },
      {
        loader: protectedLoader,
        element: <ProtectedRoute />,
        children: protectedRoutes,
      },
    ],
  },
]);
